import { fetchAuthSession } from "aws-amplify/auth";
import config from "../config";

const channelName = `vc-session-lock-${config.sessionLock.group}`;

let lockChannel = null;

function sanitizeGroup(group) {
  return String(group || "default").replace(/[^a-z0-9_-]/gi, "-").toLowerCase();
}

function cookieBaseName(name) {
  return `vc_app_lock_${sanitizeGroup(config.sessionLock.group)}_${name}`;
}

function getCookieOptions(maxAgeSeconds = 12 * 60 * 60) {
  const options = [`Path=/`, `Max-Age=${maxAgeSeconds}`, `SameSite=Lax`];

  if (config.sessionLock.cookieDomain && config.sessionLock.cookieDomain !== "localhost") {
    options.push(`Domain=${config.sessionLock.cookieDomain}`);
  }

  if (window.location.protocol === "https:") {
    options.push("Secure");
  }

  return options.join("; ");
}

function setCookie(name, value, maxAgeSeconds) {
  document.cookie = `${cookieBaseName(name)}=${encodeURIComponent(
    value
  )}; ${getCookieOptions(maxAgeSeconds)}`;
}

function getCookie(name) {
  const key = `${cookieBaseName(name)}=`;
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(key));

  if (!match) {
    return "";
  }

  return decodeURIComponent(match.slice(key.length));
}

function clearCookie(name) {
  document.cookie = `${cookieBaseName(name)}=; ${getCookieOptions(0)}`;
}

function decodeJwtPayload(token) {
  if (!token || token.split(".").length < 2) {
    return {};
  }

  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padding = (4 - (normalized.length % 4)) % 4;
    const padded = normalized.padEnd(normalized.length + padding, "=");
    return JSON.parse(atob(padded));
  } catch (error) {
    return {};
  }
}

function getLastActivityAt() {
  return Number(getCookie("last_activity_at")) || 0;
}

function getPresenceAt() {
  return Number(getCookie("presence_at")) || 0;
}

function getLockedAt() {
  return Number(getCookie("locked_at")) || 0;
}

function getStoredOriginJti() {
  return getCookie("origin_jti");
}

function broadcast(type, data = {}) {
  if (!config.sessionLock.enabled || typeof BroadcastChannel === "undefined") {
    return;
  }

  if (!lockChannel) {
    lockChannel = new BroadcastChannel(channelName);
  }

  lockChannel.postMessage({ type, ...data });
}

async function getSessionTokens(options = {}) {
  const session = await fetchAuthSession(options);
  const idToken = session.tokens?.idToken?.toString?.() || "";
  const accessToken = session.tokens?.accessToken?.toString?.() || "";
  const accessPayload = decodeJwtPayload(accessToken);

  return {
    idToken,
    accessToken,
    originJti: accessPayload.origin_jti || accessPayload.jti || "",
  };
}

function getVerifySoftwareTokenEndpoint() {
  return `https://cognito-idp.${config.cognito.REGION}.amazonaws.com/`;
}

function getVerifySoftwareTokenError(result) {
  return (
    result?.message ||
    result?.Message ||
    result?.__type ||
    "Unable to confirm the code. Please try again or log out."
  );
}

export function isSessionLockEnabled() {
  return Boolean(config.sessionLock.enabled);
}

export function isLoginPortal() {
  return Boolean(config.sessionLock.isLoginPortal);
}

export function getWarningMs() {
  return config.sessionLock.warningMs;
}

export function getTimeoutMs() {
  return config.sessionLock.timeoutMs;
}

export function getActivityThrottleMs() {
  return config.sessionLock.activityThrottleMs;
}

export function getPresenceStaleMs() {
  return config.sessionLock.presenceStaleMs;
}

export async function ensureSessionLockContext() {
  if (!isSessionLockEnabled()) {
    return null;
  }

  const { originJti } = await getSessionTokens();
  setCookie("lock_group", config.sessionLock.group);

  return { originJti };
}

export async function getSessionAwareAuthHeaders() {
  try {
    const { idToken } = await getSessionTokens();
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  } catch (error) {
    console.log("Error getting auth headers:", error);
    return {};
  }
}

export function markActivity() {
  if (!isSessionLockEnabled()) {
    return;
  }

  const now = Date.now();
  setCookie("last_activity_at", now);
  setCookie("presence_at", now);
  setCookie("state_version", now);
  broadcast("activity", { at: now });

  return now;
}

export function markPresence() {
  if (!isSessionLockEnabled()) {
    return 0;
  }

  const now = Date.now();
  setCookie("presence_at", now);
  setCookie("state_version", now);
  broadcast("presence", { at: now });

  return now;
}

export function applyLocalLock(lockedAt = Date.now()) {
  if (!isSessionLockEnabled()) {
    return;
  }

  setCookie("locked_at", lockedAt);
  setCookie("state_version", lockedAt);
  broadcast("lock", { at: lockedAt });
}

export function clearLocalLock() {
  clearCookie("locked_at");
  setCookie("state_version", Date.now());
  broadcast("unlock", { at: Date.now() });
}

export function clearSessionLockCookies() {
  clearCookie("lock_group");
  clearCookie("origin_jti");
  clearCookie("last_activity_at");
  clearCookie("presence_at");
  clearCookie("locked_at");
  clearCookie("state_version");
}

export function getInactivityDuration(now = Date.now()) {
  const lastActivityAt = getLastActivityAt();
  if (!lastActivityAt) {
    return 0;
  }

  return Math.max(0, now - lastActivityAt);
}

export function isLocallyLocked() {
  return Boolean(getLockedAt());
}

export function hasActiveParticipatingContext() {
  const presenceAt = getPresenceAt();
  if (!presenceAt) {
    return false;
  }

  return Date.now() - presenceAt <= getPresenceStaleMs();
}

export function resetParticipatingContext() {
  const now = Date.now();
  clearCookie("locked_at");
  setCookie("last_activity_at", now);
  setCookie("presence_at", now);
  setCookie("state_version", now);
  broadcast("reset", { at: now });

  return now;
}

function setSessionIdentity(originJti) {
  if (originJti) {
    setCookie("origin_jti", originJti);
  } else {
    clearCookie("origin_jti");
  }
}

function resolveParticipatingContext(originJti) {
  const storedOriginJti = getStoredOriginJti();
  const sessionChanged =
    Boolean(originJti) &&
    Boolean(storedOriginJti) &&
    storedOriginJti !== originJti;

  setSessionIdentity(originJti);

  if (sessionChanged) {
    resetParticipatingContext();
    return {
      originJti,
      startedFresh: true,
      locked: false,
      sessionChanged: true,
    };
  }

  if (hasActiveParticipatingContext()) {
    if (!getLastActivityAt()) {
      setCookie("last_activity_at", Date.now());
    }
    markPresence();
    return {
      originJti,
      startedFresh: false,
      locked: isLocallyLocked(),
      sessionChanged: false,
    };
  }

  resetParticipatingContext();
  return {
    originJti,
    startedFresh: true,
    locked: false,
    sessionChanged: false,
  };
}

export async function enterParticipatingContext() {
  if (!isSessionLockEnabled()) {
    return { startedFresh: false, locked: false };
  }

  const { originJti } = await ensureSessionLockContext();
  return resolveParticipatingContext(originJti);
}

export async function reconcileParticipatingContext() {
  if (!isSessionLockEnabled()) {
    return { startedFresh: false, locked: false };
  }

  const { originJti } = await ensureSessionLockContext();
  return resolveParticipatingContext(originJti);
}

export function getWarningState(now = Date.now()) {
  const inactivity = getInactivityDuration(now);
  const warningMs = getWarningMs();
  const timeoutMs = getTimeoutMs();

  return {
    inactivity,
    shouldWarn: inactivity >= warningMs && inactivity < timeoutMs,
    shouldLock: inactivity >= timeoutMs,
    remainingSeconds: Math.max(0, Math.ceil((timeoutMs - inactivity) / 1000)),
    msUntilWarning: Math.max(0, warningMs - inactivity),
    msUntilLock: Math.max(0, timeoutMs - inactivity),
  };
}

export async function lockSession() {
  const lockedAt = Date.now();
  applyLocalLock(lockedAt);
  return { locked: true, lockedAt };
}

export async function verifyUnlock(code) {
  const trimmedCode = String(code || "").trim();
  if (!trimmedCode) {
    throw new Error("Enter your MFA code to continue.");
  }

  const { accessToken } = await getSessionTokens();
  if (!accessToken) {
    throw new Error("Session expired. Please log in again.");
  }

  const response = await fetch(getVerifySoftwareTokenEndpoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.VerifySoftwareToken",
    },
    body: JSON.stringify({
      AccessToken: accessToken,
      UserCode: trimmedCode,
    }),
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok || result?.Status !== "SUCCESS") {
    throw new Error(getVerifySoftwareTokenError(result));
  }

  clearLocalLock();
  markActivity();

  return result;
}

export function subscribeToLockEvents(callback) {
  if (!isSessionLockEnabled() || typeof BroadcastChannel === "undefined") {
    return () => {};
  }

  if (!lockChannel) {
    lockChannel = new BroadcastChannel(channelName);
  }

  const handler = (event) => callback(event.data);
  lockChannel.addEventListener("message", handler);

  return () => {
    lockChannel.removeEventListener("message", handler);
    lockChannel.close();
    lockChannel = null;
  };
}
